import React, { useState, useEffect } from 'react';
import { Table, Button, Icon, Loader, Message, Input, Confirm } from 'semantic-ui-react';
import axios from 'axios';

const UnsubscribedUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resubTarget, setResubTarget] = useState(null);
    const [resubbing, setResubbing] = useState(false);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/admin/emails/unsubscribed');
            setUsers(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleResubscribe = async () => {
        if (!resubTarget) return;
        setResubbing(true);
        try {
            await axios.post('/admin/emails/resubscribe', { userId: resubTarget.userId });
            setUsers(prev => prev.filter(u => u.userId !== resubTarget.userId));
            setConfirmOpen(false);
            setResubTarget(null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setResubbing(false);
        }
    };

    const filtered = users.filter(u => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (u.email || '').toLowerCase().includes(term)
            || (u.firstName || '').toLowerCase().includes(term)
            || (u.lastName || '').toLowerCase().includes(term);
    });

    if (loading) return <Loader active inline="centered" content="Loading unsubscribed users..." />;
    if (error) return <Message negative header="Error" content={error} />;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <p style={{ color: '#445566', fontSize: '14px', margin: 0 }}>
                    {users.length} user{users.length !== 1 ? 's' : ''} have unsubscribed from emails.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Input
                        icon="search"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: '300px' }}
                    />
                    <Button icon="refresh" basic color="blue" onClick={fetchUsers} />
                </div>
            </div>

            {filtered.length === 0 ? (
                <Message info content={searchTerm ? 'No matching users found.' : 'No unsubscribed users.'} />
            ) : (
                <Table celled striped>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Name</Table.HeaderCell>
                            <Table.HeaderCell>Email</Table.HeaderCell>
                            <Table.HeaderCell>Unsubscribed Date</Table.HeaderCell>
                            <Table.HeaderCell>Source</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: '120px' }}>Action</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {filtered.map(user => (
                            <Table.Row key={user.userId}>
                                <Table.Cell>{`${user.firstName} ${user.lastName}`.trim() || 'Unknown'}</Table.Cell>
                                <Table.Cell>{user.email}</Table.Cell>
                                <Table.Cell>
                                    {user.unsubscribedAt ? new Date(user.unsubscribedAt).toLocaleDateString() : 'Unknown'}
                                </Table.Cell>
                                <Table.Cell>{user.unsubscribeSource}</Table.Cell>
                                <Table.Cell>
                                    <Button
                                        size="small"
                                        color="green"
                                        basic
                                        onClick={() => { setResubTarget(user); setConfirmOpen(true); }}
                                    >
                                        <Icon name="redo" /> Resubscribe
                                    </Button>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}

            <Confirm
                open={confirmOpen}
                onCancel={() => { setConfirmOpen(false); setResubTarget(null); }}
                onConfirm={handleResubscribe}
                header="Resubscribe User"
                content={`Are you sure you want to resubscribe ${resubTarget?.email || 'this user'} to email notifications?`}
                confirmButton={{ content: 'Resubscribe', loading: resubbing }}
            />
        </div>
    );
};

export default UnsubscribedUsers;
